import { useState, useEffect } from 'react'
import { Input, Typography, Popover, EyeButton } from '~components'
import { useStore, useNavigate, usePathValidation } from '~hooks'
import { string, object, boolean } from '~utils/validation'
import { Values } from './interfaces'
import * as Styled from './styled'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Auth } from '~services'
import { useRequest } from 'ahooks'
import { trackAuthentication } from '~utils/analytics'
import { registerPayloadFormater } from '~utils/formatters'
import { urlHandler } from '~utils/path'
import { track } from '@amplitude/analytics-browser'
import isMobile from 'is-mobile'

function Password() {
  const defaultValues = {
    password: '',
    remember: false,
    data_usage_terms_accepted: false,
    terms: false,
  }
  const { loading, runAsync } = useRequest(Auth.register, {
    manual: true,
  })
  const termsOfUseUrl = `${process.env.REACT_APP_FRETEBRAS_SITE}/termos-de-uso`
  const privacyPolicyUrl = `${process.env.REACT_APP_FRETEBRAS_SITE}/politica-de-privacidade`
  const [isPasswordVisible, setPasswordVisible] = useState(false)
  const [isPopoverVisible, setPopoverVisible] = useState(false)
  const { update, ...store } = useStore()
  const navigate = useNavigate()
  const payload = registerPayloadFormater(store)
  const schema = object({
    password: string()
      .required()
      .min(8, 'Pelo menos 8 caracteres')
      .max(72, '')
      .atLeastOneCharacter('Pelo menos uma letra')
      .atLeastOneNumber('Pelo menos um número'),
    remember: boolean(),
    data_usage_terms_accepted: boolean().required().isTrue(),
    terms: boolean().required().isTrue(),
  })
  const {
    handleSubmit,
    formState: { isValid, dirtyFields, errors },
    register,
    setFocus,
  } = useForm({
    defaultValues,
    mode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(schema),
  })
  const popoverMessages = [
    {
      text: 'Pelo menos 8 caracteres',
      isSuccess: !errors?.password?.types?.min,
    },
    {
      text: 'Pelo menos uma letra',
      isSuccess: !errors?.password?.types?.atLeastOneCharacter,
    },
    {
      text: 'Pelo menos um número',
      isSuccess: !errors?.password?.types?.atLeastOneNumber,
    },
  ]

  useEffect(() => {
    track('cadastro_senha', {
      tipo_veiculo: store.register.vehicleType,
      tipo_carroceria: store.register.vehicleBody,
      rastreador: store.register.vehicleTracking,
    })
  }, [])

  useEffect(() => {
    update({ register: { progress: 90 } })
  }, [])

  usePathValidation([
    'cpf',
    'plate',
    'register.cellphone',
    'register.fullName',
    'register.rntrc',
    'register.vehicleType',
    'register.vehicleBody',
    'register.selfie',
  ])

  function handleClickShowPassword() {
    setFocus('password')
    setPasswordVisible((prevState) => !prevState)
  }
  function handleChange(event: { target: { value: string } }) {
    const value = event.target.value
    if (value.includes(' ')) event.target.value = value.replace(/ /g, '')
  }
  function handlePaste(event: { preventDefault: () => void }) {
    event.preventDefault()
  }
  function handleFocus() {
    setPopoverVisible(true)
  }
  function handleBlur() {
    setPopoverVisible(false)
  }
  async function onSubmit(values: Values) {
    const response = await runAsync({
      ...payload,
      ...values,
    })

    trackAuthentication(response.user_uuid)
    update({ uuid: response.user_uuid })
    navigate('/cadastrar/sucesso', { state: { previousPath: '/cadastrar/senha' } })
  }

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        aria-labelledby="form-plate-register-password-title"
      >
        <Styled.Title id="form-plate-register-password-title">
          Agora, crie sua senha
        </Styled.Title>
        <Typography as="label">Senha</Typography>
        <Styled.WrapperRelative>
          {dirtyFields?.password && isPopoverVisible && (
            <Popover title="Sua senha deve conter:" messages={popoverMessages} />
          )}
          <Input
            onPaste={handlePaste}
            onChange={handleChange}
            autoFocus
            onFocus={handleFocus}
            onBlur={handleBlur}
            role="password"
            name="password"
            type={isPasswordVisible ? 'text' : 'password'}
            register={register}
            data-testid="password"
            maxLength={72}
          />
          <EyeButton visibility={isPasswordVisible} onClick={handleClickShowPassword} />
        </Styled.WrapperRelative>
        {!isMobile() && (
          <Styled.Checkbox
            register={register}
            label="Me manter conectado"
            name="remember"
          />
        )}
        <Styled.SpaceCheck>
          <Styled.Checkbox
            register={register}
            name="terms"
            alignItems="flex-start"
            height="auto"
            data-testid="check-privacy"
            label={
              <>
                <Styled.Message>
                  Ao entrar você concorda com os{' '}
                  <Styled.Link href={urlHandler(termsOfUseUrl)} target="_blank">
                    Termos de Uso
                  </Styled.Link>{' '}
                  e com a{' '}
                  <Styled.Link href={urlHandler(privacyPolicyUrl)} target="_blank">
                    Política de Privacidade
                  </Styled.Link>{' '}
                  da Fretebras.
                </Styled.Message>
              </>
            }
          />
          <Styled.Checkbox
            register={register}
            name="data_usage_terms_accepted"
            data-testid="check-accept-data"
            alignItems="flex-start"
            height="auto"
            label={
              <>
                <Styled.Message>
                  Aceito que a Fretebras utilize os meus dados pessoais coletados por
                  empresas de seu grupo econômico (como a SAT, CargoX e AGFrete) para
                  gerar a minha pontuação na plataforma, conforme a{' '}
                  <Styled.Link href={urlHandler(privacyPolicyUrl)} target="_blank">
                    Política de Privacidade
                  </Styled.Link>{' '}
                  e os
                  <Styled.Link href={urlHandler(termsOfUseUrl)} target="_blank">
                    Termos de Uso
                  </Styled.Link>
                </Styled.Message>
              </>
            }
          />
        </Styled.SpaceCheck>
        <Styled.SubmitButton width="100%" disabled={!isValid} loading={loading}>
          Continuar
        </Styled.SubmitButton>
      </form>
    </>
  )
}

export default Password
